/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popup } from '../Popup';
/**
 * ToolbarSelection Component
 *
 * Renders a "N selected" trigger button (checked checkbox + count + chevron)
 * that opens a menu with:
 *   - Select all on this page  (when `selectPageLabel` + `onSelectPage` are supplied)
 *   - Select all matching items (when `selectMatchingLabel` + `onSelectMatching` are supplied)
 *   - Clear selection           (when `clearLabel` + `onClear` are supplied)
 *
 * Falls back to the legacy badge+button rendering when the new menu API props
 * (`selectedLabel`, `selectPageLabel`, `clearLabel`) are absent, so existing
 * callers that have not migrated keep working unchanged.
 *
 * Hidden entirely when count === 0.
 */
export function ToolbarSelection({ count, totalCount: _totalCount, selectAll, onSelectAll, selectAllLabel, selectedLabel, selectPageLabel, onSelectPage, selectMatchingLabel, onSelectMatching, clearLabel, onClear }) {
    if (count === 0) {
        return null;
    }
    const useMenu = selectedLabel !== undefined || selectPageLabel !== undefined || clearLabel !== undefined;
    if (!useMenu) {
        // Legacy fallback: badge + inline "Select all" button
        const showSelectAll = !selectAll;
        return (React.createElement("div", { className: "qmb-ui-toolbar__selection" },
            React.createElement("div", { className: "qmb-ui-toolbar__selection-count" },
                React.createElement("span", { className: "qmb-ui-toolbar__count-badge" }, count)),
            showSelectAll && (React.createElement("div", { className: "qmb-ui-toolbar__selection-all" },
                React.createElement("button", { type: "button", className: "qmb-ui-button", onClick: onSelectAll }, selectAllLabel)))));
    }
    return (React.createElement(ToolbarSelectionMenu, { selectedLabel: selectedLabel !== null && selectedLabel !== void 0 ? selectedLabel : String(count), selectPageLabel: selectPageLabel, onSelectPage: onSelectPage, selectMatchingLabel: selectMatchingLabel, onSelectMatching: onSelectMatching, clearLabel: clearLabel, onClear: onClear }));
}
ToolbarSelection.displayName = 'ToolbarSelection';
function ToolbarSelectionMenu({ selectedLabel, selectPageLabel, onSelectPage, selectMatchingLabel, onSelectMatching, clearLabel, onClear }) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const itemRefs = useRef([]);
    const openMenu = useCallback(() => setOpen(true), []);
    const closeMenu = useCallback(() => {
        var _a;
        setOpen(false);
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    useEffect(() => {
        var _a;
        if (!open)
            return;
        (_a = itemRefs.current.find((el) => el !== null)) === null || _a === void 0 ? void 0 : _a.focus();
    }, [open]);
    const handleTriggerKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
        }
        else if (e.key === 'Escape') {
            closeMenu();
        }
    }, [closeMenu]);
    const handleMenuKeyDown = useCallback((e) => {
        const items = itemRefs.current.filter((el) => el !== null);
        if (items.length === 0)
            return;
        const current = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            items[(current + 1) % items.length].focus();
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            items[(current - 1 + items.length) % items.length].focus();
        }
        else if (e.key === 'Home') {
            e.preventDefault();
            items[0].focus();
        }
        else if (e.key === 'End') {
            e.preventDefault();
            items[items.length - 1].focus();
        }
        else if (e.key === 'Escape' || e.key === 'Tab') {
            e.preventDefault();
            closeMenu();
        }
    }, [closeMenu]);
    const handleSelectPage = useCallback(() => {
        closeMenu();
        onSelectPage === null || onSelectPage === void 0 ? void 0 : onSelectPage();
    }, [closeMenu, onSelectPage]);
    const handleSelectMatching = useCallback(() => {
        closeMenu();
        onSelectMatching === null || onSelectMatching === void 0 ? void 0 : onSelectMatching();
    }, [closeMenu, onSelectMatching]);
    const handleClear = useCallback(() => {
        closeMenu();
        onClear === null || onClear === void 0 ? void 0 : onClear();
    }, [closeMenu, onClear]);
    const showPage = selectPageLabel !== undefined && onSelectPage !== undefined;
    const showMatching = selectMatchingLabel !== undefined && onSelectMatching !== undefined;
    const showClear = clearLabel !== undefined && onClear !== undefined;
    const pageIndex = showPage ? 0 : -1;
    const matchingIndex = showMatching ? Number(showPage) : -1;
    const clearIndex = showClear ? Number(showPage) + Number(showMatching) : -1;
    return (React.createElement("div", { className: "qmb-ui-toolbar__selection" },
        React.createElement("button", { ref: triggerRef, type: "button", className: "qmb-ui-toolbar__selection-trigger", "aria-haspopup": "menu", "aria-expanded": open, onClick: open ? closeMenu : openMenu, onKeyDown: handleTriggerKeyDown },
            React.createElement("i", { className: "fa-solid fa-square-check qmb-ui-toolbar__selection-checkbox-icon", "aria-hidden": "true" }),
            React.createElement("span", { className: "qmb-ui-toolbar__selection-label" }, selectedLabel),
            React.createElement("i", { className: `fa-light ${open ? 'fa-chevron-up' : 'fa-chevron-down'} qmb-ui-toolbar__selection-chevron`, "aria-hidden": "true" })),
        React.createElement(Popup, { anchor: triggerRef.current, show: open, onClose: closeMenu, position: "bottom-left" },
            React.createElement("ul", { className: "qmb-ui-toolbar__selection-menu", role: "menu", onKeyDown: handleMenuKeyDown },
                showPage && (React.createElement("li", { role: "none", className: "qmb-ui-toolbar__selection-menu-item" },
                    React.createElement("button", { type: "button", role: "menuitem", ref: (el) => {
                            itemRefs.current[pageIndex] = el;
                        }, onClick: handleSelectPage }, selectPageLabel))),
                showMatching && (React.createElement("li", { role: "none", className: "qmb-ui-toolbar__selection-menu-item" },
                    React.createElement("button", { type: "button", role: "menuitem", ref: (el) => {
                            itemRefs.current[matchingIndex] = el;
                        }, onClick: handleSelectMatching }, selectMatchingLabel))),
                showClear && (React.createElement("li", { role: "none", className: "qmb-ui-toolbar__selection-menu-item" },
                    React.createElement("button", { type: "button", role: "menuitem", ref: (el) => {
                            itemRefs.current[clearIndex] = el;
                        }, onClick: handleClear }, clearLabel)))))));
}
